import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CloudSun, Wind, Drop, Thermometer, Waveform, MapPin, ArrowSquareOut } from "@phosphor-icons/react";

const WMO = {
  0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast", 45: "Fog", 48: "Rime fog",
  51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle", 61: "Light rain", 63: "Rain", 65: "Heavy rain",
  71: "Light snow", 73: "Snow", 75: "Heavy snow", 80: "Rain showers", 81: "Showers", 82: "Violent showers",
  95: "Thunderstorm", 96: "Storm + hail", 99: "Severe storm",
};

const PRESETS = [
  { name: "New York", lat: 40.7128, lon: -74.006 },
  { name: "Los Angeles", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago", lat: 41.8781, lon: -87.6298 },
  { name: "London", lat: 51.5074, lon: -0.1278 },
];

function magColor(m) { return m >= 5 ? "#FF3B30" : m >= 4 ? "#FF9F0A" : "#32D74B"; }

export default function AreaIntel() {
  const [loc, setLoc] = useState(PRESETS[0]);
  const [weather, setWeather] = useState(null);
  const [quakes, setQuakes] = useState([]);

  useEffect(() => {
    api.get(`/intel/weather?lat=${loc.lat}&lon=${loc.lon}`).then((r) => setWeather(r.data.current)).catch(() => toast.error("Weather feed unavailable"));
  }, [loc]);
  useEffect(() => {
    api.get("/intel/earthquakes").then((r) => setQuakes(r.data.quakes)).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="intel-locations">
        {PRESETS.map((p) => (
          <button key={p.name} onClick={() => setLoc(p)} data-testid={`loc-${p.name.replace(/\s/g, "-")}`}
            className={`px-3 py-1.5 text-xs font-mono-data border transition-colors ${loc.name === p.name ? "bg-[#007AFF]/15 text-[#3395FF] border-[#007AFF]/50" : "bg-[#1A1A1E] text-[#71717A] border-white/10 hover:text-white"}`}>
            <MapPin size={11} className="inline mr-1" />{p.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 bg-[#121214] border border-white/10 p-5" data-testid="weather-card">
          <div className="flex items-center gap-2 mb-4">
            <CloudSun size={18} weight="bold" className="text-[#007AFF]" />
            <h2 className="font-display font-black uppercase tracking-tighter text-sm">Live Weather · {loc.name}</h2>
          </div>
          {weather ? (
            <>
              <div className="flex items-end gap-3 mb-4">
                <span className="font-mono-data text-5xl">{Math.round(weather.temperature_2m)}°</span>
                <span className="text-[#A1A1AA] mb-2">{WMO[weather.weather_code] || "—"}</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#1A1A1E] border border-white/10 p-3"><Thermometer size={16} className="text-[#FF9F0A] mb-1" /><p className="font-mono-data text-lg">{Math.round(weather.apparent_temperature)}°</p><p className="text-[10px] text-[#71717A] uppercase">Feels</p></div>
                <div className="bg-[#1A1A1E] border border-white/10 p-3"><Wind size={16} className="text-[#0A84FF] mb-1" /><p className="font-mono-data text-lg">{Math.round(weather.wind_speed_10m)}</p><p className="text-[10px] text-[#71717A] uppercase">km/h</p></div>
                <div className="bg-[#1A1A1E] border border-white/10 p-3"><Drop size={16} className="text-[#32D74B] mb-1" /><p className="font-mono-data text-lg">{weather.relative_humidity_2m}%</p><p className="text-[10px] text-[#71717A] uppercase">Humidity</p></div>
              </div>
              <p className="text-[10px] text-[#71717A] font-mono-data mt-4">Source: Open-Meteo · live</p>
            </>
          ) : <p className="text-sm text-[#71717A] font-mono-data">Loading weather…</p>}
        </div>

        <div className="lg:col-span-7 bg-[#121214] border border-white/10">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Waveform size={16} weight="bold" className="text-[#FF3B30]" />
            <h2 className="font-display font-black uppercase tracking-tighter text-sm">Global Seismic Feed (24h, M2.5+)</h2>
          </div>
          <div className="p-3 space-y-1.5 max-h-[320px] overflow-y-auto" data-testid="quake-feed">
            {quakes.length === 0 && <p className="text-sm text-[#71717A] font-mono-data p-2">Loading feed…</p>}
            {quakes.map((q) => (
              <a key={q.id} href={q.url} target="_blank" rel="noreferrer" className="flex items-center justify-between border-b border-white/5 pb-1.5 hover:bg-white/5 px-1 transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono-data text-sm w-10 shrink-0" style={{ color: magColor(q.mag) }}>{q.mag?.toFixed(1)}</span>
                  <span className="text-xs text-[#A1A1AA] truncate">{q.place}</span>
                </div>
                <span className="font-mono-data text-[10px] text-[#71717A] flex items-center gap-1 shrink-0">{new Date(q.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} <ArrowSquareOut size={10} /></span>
              </a>
            ))}
          </div>
          <p className="text-[10px] text-[#71717A] font-mono-data px-4 py-2 border-t border-white/10">Source: USGS · live public feed</p>
        </div>
      </div>
    </div>
  );
}
